import { RegionShared } from "@1games-ru/region-shared";
import { GetBusinessInfoResponse } from "@1games-ru/region-shared/dist/backend/requests/business";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import rpc from "@1games-ru/rage-rpc";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import toast from "@/domains/toasts/helpers/toast";

export default function useShowBusinessModal() {
  const { t, i18n } = useTranslation("business");

  const [businessId, setBusinessId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState<GetBusinessInfoResponse | null>(null);
  const [isBuying, setIsBuying] = useState(false);

  const exit = useCallback(() => GameMpProvider.closeCurrentInterface(true), []);

  const load = useCallback(async () => {
    if (businessId) {
      setIsLoading(true);

      try {
        const response = await BusinessMpProvider.getInfo(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          setInfo(response.data);
        } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("business.toast.infoLoadError"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.infoLoadError"));
      } finally {
        setIsLoading(false);
      }
    }
  }, [businessId, i18n, t]);

  useEffect(() => {
    if (businessId) load();
  }, [businessId, load]);

  const buyBusiness = useCallback(
    async (paymentType: PaymentType, cb: () => void) => {
      if (businessId) {
        setIsBuying(true);

        try {
          const response = await BusinessMpProvider.buyBusiness(businessId, paymentType);

          if (response instanceof RpcMpProviderSuccessResponse) {
            cb();
            toast.success(t("business.toast.buyBusinessSuccess"));
          } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.buyBusinessError"));
          }
        } catch (e) {
          console.error(e);
          toast.error(t("business.toast.buyBusinessError"));
        } finally {
          setIsBuying(false);
        }
      }
    },
    [businessId, i18n, t],
  );

  useLayoutEffect(() => {
    rpc.register(RegionShared.Cef.Events.Business.BusinessSignShow, async (state) => {
      setBusinessId(state?.businessId);
    });

    rpc.register(RegionShared.Cef.Events.Business.BusinessSignHide, async () => {
      setBusinessId(null);
      setInfo(null);
    });

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Business.BusinessSignShow);
      rpc.unregister(RegionShared.Cef.Events.Business.BusinessSignHide);
      setBusinessId(null);
      setInfo(null);
    };
  }, []);

  return { isLoading, info, exit, buyBusiness, isBuying };
}
